import React, {
  createRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Tooltip from "react-tooltip";
import ReactPannellum, {
  addHotSpot,
  addScene,
  getAllScenes,
  getCurrentScene,
  getViewer,
  isLoaded,
  loadScene,
  removeHotSpot,
  removeScene,
} from "react-pannellum";
import imageCompression from "browser-image-compression";

import { createRoot } from "react-dom/client";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import fullScreenMaximizeImage from "../../../assets/panorama/fullscreen-maximize.png";
import toggleHotspotImage from "../../../assets/panorama/toggle-hotspot.png";
import cycleDayImage from "../../../assets/panorama/cycle-day.png";
import cycleNightImage from "../../../assets/panorama/cycle-night.png";
import materialImage from "../../../assets/panorama/material.png";
import floorPlanImage from "../../../assets/panorama/floor-plan.png";

// * Hotspot icons
import hotspotPinImage from "../../../assets/panorama/hotspot.gif";
import hotspotPin2Image from "../../../assets/panorama/icons/hotspot-3.gif";
import hotspotPin3Image from "../../../assets/panorama/icons/hotspot-4.gif";
import imagePinImage from "../../../assets/panorama/icons/image.gif";
import leftTurnImage from "../../../assets/panorama/icons/left.gif";
import rightTurnImage from "../../../assets/panorama/icons/right.gif";
import forwardTurnImage from "../../../assets/panorama/icons/forward.gif";
import leftTurn2Image from "../../../assets/panorama/icons/left-2.gif";
import rightTurn2Image from "../../../assets/panorama/icons/right-2.gif";
import voiceMemoImage from "../../../assets/panorama/icons/voice.gif";

import { ReactComponent as ArrowRightSvg } from "../../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowLeftSvg } from "../../../assets/icons/arrow-left.svg";
import { ReactComponent as FileUploaderPlaceholderSvg } from "../../../assets/icons/fileuploader-placeholder.svg";
import { ReactComponent as TrashSvg } from "../../../assets/icons/trash.svg";

import NewProjectContext from "../../../contexts/NewProjectContext";

import "../../../styles/panorama.scss";
import { PAGE_STATE, langComponent } from "../../../constants";
import SelectMusic from "../SelectMusic";
import AppContext from "../../../AppContext";
import { uuidv4 } from "../../../helpers/common.helper";
import _ from "lodash";
import MaterialSelection from "./MaterialSelection";
import MaterialSwitcher from "./MaterialSwitcher";
import CycleWizard from "./CycleWizard";
import AddHotspotWizard from "./AddHotspot";
import Button from "../../common/Button";
import { EditHotspot } from "./EditHotspot";
import ToolTipInfo from "./TooltipInfo";

let viewer = null;

const HOTSPOT_TYPE = {
  WALK_WAY: 1,
  INFO: 2,
  VOICE_MEMO: 3,
  PICTURE: 4,
};

var musicPlayer = new Audio();

export const Context = React.createContext({
  images: [],
  selectedImageName: undefined,
  setSelectedImageName: () => {},
  materialGroups: [],
  setMaterialGroups: () => {},
  cycles: [],
  setCycles: () => {},
  latestPitchYaw: undefined,
  scenes: [],
  setScenes: () => {},
  switchToPlaceHotspot: (detail) => {},
  closeOverlay: () => {},
  tooltipInfo: false,
  panoramaCovers: [],
  addNewPanoramaImage: (files) => {},
  editHotspot: undefined,
  setEditHotspot: (detail) => {},
  removeHotspot: (sceneName, hotspotId) => {},
  addHotspot: (sceneName, details) => {},
});

const SetupHotspot = () => {
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.project);
  const TranslationCommon = getLang(langComponent.common);
  const { goForward, goBackward, details, setDetails } =
    useContext(NewProjectContext);
  const pannellumRef = useRef();
  const [images, setImages] = useState(details.panoramaImages ?? []);
  const [selectedImageName, setSelectedImageName] = useState(
    images.length === 0 ? undefined : images[0].name
  );
  const [scenes, setScenes] = useState(details.scenes || []);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const [cycles, setCycles] = useState(details.cycles || []);
  const [materialGroups, setMaterialGroups] = useState(
    details.materialGroups || []
  );
  const [music, setMusic] = useState(details.music || { _id: "", file: "" });
  const [dragActive, setDragActive] = useState(false);
  const [floorPlans, setFloorPlans] = useState(details?.floorPlans || []);
  const [panoramaCovers, setPanoramaCovers] = useState(
    details.panoramaCovers ?? []
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDisplayMaterialWizard, setIsDisplayMaterialWizard] = useState(false);
  const [isDisplayMaterialSwitcher, setIsDisplayMaterialSwitcher] =
    useState(false);
  const [isDisplayCycleWizard, setIsDisplayCycleWizard] = useState(false);
  const [isDisplayHotspotWizard, setIsDisplayHotspotWizard] = useState(false);
  const [latestPitchYaw, setLatestPitchYaw] = useState(undefined);
  const [isDisplayHotspotPlacement, setIsDisplayHotspotPlacement] =
    useState(false);
  const [isDisplayTooltipInfo, setIsDisplayTooltipInfo] = useState(false);
  const [watermark, setWatermark] = useState(details?.watermark ?? undefined);
  const [init, setInit] = useState(false);
  const [floorPlanNames, setFloorPlanNames] = useState(
    details?.floorPlanNames ?? []
  );
  const [editHotspot, setEditHotspot] = useState(undefined);

  const _baseConfigs = {
    autoLoad: true,
    orientationOnByDefault: false,
    keyboardZoom: false,
    disableKeyboardCtrl: true,
    showControls: false,
    pitch: 0,
    yaw: 0,
    // minHfov: 10,
    // maxHfov: 170,
  };

  useEffect(() => {
    // * Init and combine scenes
    const _scenes = [];

    for (let i = 0; i < images.length; i++) {
      const image = images[i];

      const _scene = scenes.find((s) => s.imageName === image.name);

      // * Skip if scene already exists
      if (_scene) {
        _scenes.push({ ..._scene, imageIndex: i });
        continue;
      }

      _scenes.push({
        name: "",
        imageIndex: i,
        imageName: image.name,
        hotspots: [],
      });
    }

    setScenes(_scenes);
    setPageState(PAGE_STATE.IDLE);

    document.addEventListener("wheel", handleZoom);

    return () => {
      setScenes([]);

      viewer?.destroy();
      viewer = null;

      document.removeEventListener("wheel", handleZoom);

      // * Stop music player if exists
      if (musicPlayer) {
        musicPlayer.pause();
        musicPlayer = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!init) return;

    // * Add images
    for (let i = 0; i < images.length; i++) {
      // * Add scene only for other images
      const image = images[i];

      const sceneId = image.name;
      const imageSource = URL.createObjectURL(image);

      addScene(
        sceneId,
        {
          ..._baseConfigs,
          imageSource,
        },
        () => {
          if (i === 0) {
            loadScene(sceneId);

            viewer = getViewer();
          }
        }
      );

      const hotspots = scenes.find((s) => s.imageIndex === i)?.hotspots ?? [];

      for (const hotspot of hotspots) {
        const {
          type,
          id,
          title,
          pitch,
          yaw,
          desc,
          sceneIndex,
          sceneName,
          icon,
          customIcon,
          file,
        } = hotspot;

        // * Add hotspots
        addHotSpot(
          {
            id: id,
            pitch,
            yaw,
            text: title,
            type: "custom",
            cssClass: "temp-hotspot",
            createTooltipFunc: createTempHotspot,
            createTooltipArgs: {
              id: id,
              text: title,
              desc,
              sceneIndex,
              sceneName,
              type,
              iconIndex: icon,
              customIcon,
              file,
              pitch,
              yaw,
            },
          },
          sceneId
        );
      }
    }
  }, [init]);

  const handleZoom = () => {};

  // * Change scene when image selected changed
  useEffect(() => {
    if (!viewer) return;

    const _newScene = scenes.find((s) => s.imageName === selectedImageName);

    if (!_newScene) return;

    if (viewer.getScene() === selectedImageName) return;

    viewer.loadScene(_newScene.imageName);
  }, [selectedImageName]);

  // * Play/Change music when source changed
  // useEffect(() => {
  //   handlePlayBackgroundMusic();
  // }, [music]);

  /**
   * * Handle playing music
   */
  // const handlePlayBackgroundMusic = () => {
  //   if (musicPlayer) {
  //     musicPlayer.pause();
  //     musicPlayer = null;
  //   }

  //   if (!music.file) return;

  //   musicPlayer = new Audio(music.file);
  //   musicPlayer.loop = true;
  //   musicPlayer.play();
  // };

  // * (Un)Bind mouse up event to support hotspot placement
  useEffect(() => {
    if (isDisplayHotspotPlacement)
      document.addEventListener(
        "mouseup",
        handleAddHotspotPlacementMouseUpEvent
      );

    if (!isDisplayHotspotPlacement)
      document.removeEventListener(
        "mouseup",
        handleAddHotspotPlacementMouseUpEvent
      );

    return () => {
      document.removeEventListener(
        "mouseup",
        handleAddHotspotPlacementMouseUpEvent
      );
    };
  }, [isDisplayHotspotPlacement]);

  // * Handle Mouse Up Event
  const handleAddHotspotPlacementMouseUpEvent = (evt) => {
    if (!evt.target.className.includes("pnlm")) return;

    // * Get only needed fields
    const { id, type, icon, file } = isDisplayHotspotPlacement;

    const [pitch, yaw] = viewer.mouseEventToCoords(evt);
    setLatestPitchYaw([pitch, yaw]);

    // * If id is provided then fade the current one
    if (id && id !== "temp") {
      const _hotspot = document.getElementById(`temp-hotspot__pin--${id}`);
      if (!_hotspot.classList.contains("temp-hotspot__pin--inactive")) {
        _hotspot.classList.add("temp-hotspot__pin--inactive");
      }
    }

    // * Remove previous temp hotspots
    viewer.removeHotSpot("temp");

    viewer.addHotSpot(
      {
        id: "temp",
        pitch,
        yaw,
        type: "custom",
        cssClass: "temp-hotspot",
        createTooltipFunc: createTempHotspot,
        createTooltipArgs: {
          id: "temp",
          iconIndex: icon,
          type,
          file,
          pitch,
          yaw,
        },
      },
      selectedImageName.toString()
    );
  };

  const handleCloseOverlay = () => {
    viewer.removeHotSpot("temp");
    setIsDisplayMaterialWizard(false);
    setIsDisplayMaterialSwitcher(false);
    setIsDisplayCycleWizard(false);
    setIsDisplayHotspotWizard(false);
    setLatestPitchYaw(undefined);
    setIsDisplayHotspotPlacement(undefined);
    setIsDisplayTooltipInfo(undefined);
    setEditHotspot(undefined);
    // handlePlayBackgroundMusic();
  };

  /**
   * Create Temp Hotspot after creation
   */
  const createTempHotspot = (hotSpotDiv, args) => {
    const { id, text, desc, sceneName, type, iconIndex, customIcon, file } =
      args;

    const Element = () => {
      const getIconToRender = () => {
        if (type === HOTSPOT_TYPE.INFO) return hotspotPinImage;
        if (type === HOTSPOT_TYPE.PICTURE) return imagePinImage;
        if (type === HOTSPOT_TYPE.VOICE_MEMO) return voiceMemoImage;

        if (iconIndex === -1) {
          return URL.createObjectURL(customIcon);
        }

        if (iconIndex === 1) {
          return forwardTurnImage;
        }

        if (iconIndex === 2) {
          return leftTurnImage;
        }

        if (iconIndex === 3) {
          return leftTurn2Image;
        }

        if (iconIndex === 4) {
          return rightTurnImage;
        }

        if (iconIndex === 5) {
          return rightTurn2Image;
        }

        if (iconIndex === 6) {
          return hotspotPinImage;
        }

        if (iconIndex === 7) {
          return hotspotPin2Image;
        }

        if (iconIndex === 8) {
          return hotspotPin3Image;
        }

        return hotspotPinImage;
      };

      return (
        <img
          className={`temp-hotspot__pin`}
          id={`temp-hotspot__pin--${id}`}
          src={getIconToRender()}
          onClick={() => {
            if (id === "temp") return;

            if (type === HOTSPOT_TYPE.WALK_WAY) {
              setSelectedImageName(sceneName);
              return;
            }

            setIsDisplayTooltipInfo({
              title: text,
              desc,
              type,
              file,
            });
          }}
          onContextMenu={(e) => {
            e.preventDefault();

            if (id === "temp") return;

            setEditHotspot(args);
          }}
        />
      );
    };

    const root = createRoot(hotSpotDiv);
    root.render(<Element />);
  };

  /**
   * Toggle Hotspot creations
   */
  const handleToggleHotspot = () => {
    setIsFullscreen((curr) => !curr);
  };

  useEffect(() => {
    if (!viewer) {
      return;
    }

    viewer.resize();
  }, [isFullscreen]);

  const handleContinue = () => {
    // * Validate
    if (floorPlanNames.find((fn) => fn.name === undefined || fn.name === "")) {
      alert("Some floor plans doesn't have a name");
      return;
    }

    setDetails((curr) => ({
      ...curr,
      scenes,
      cycles,
      materialGroups,
      floorPlans,
      floorPlanNames,
      music,
      panoramaImages: images,
      watermark,
    }));

    getViewer().destroy();

    goForward();
  };

  const handleReturn = () => {
    setDetails((curr) => ({
      ...curr,
      scenes,
      cycles,
      materialGroups,
      floorPlans,
      music,
    }));
    goBackward();
  };

  const handleFloorPlanFiles = (files) => {
    if (files.length === 0) return;

    setFloorPlans((curr) => {
      // * Filter out duplicate files
      const existingFilenames = curr.map((c) => c.name);

      const _files = [...files].filter(
        (file) => !existingFilenames.includes(file.name)
      );

      setFloorPlanNames((curr) => [
        ...curr,
        ..._files.map((f) => ({ fileName: f.name, name: f.name })),
      ]);

      return [...curr, ..._files];
    });
  };

  const handleDragDropEvents = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if ((e.type === "dragenter" || e.type === "dragover") && !dragActive) {
      setDragActive(true);
    }

    if ((e.type === "dragleave" || e.type === "drop") && dragActive) {
      setDragActive(false);
    }

    if (e.type === "drop") {
      handleFloorPlanFiles(e.dataTransfer.files);
    }
  };

  const _handleConfirmHotspotPlacement = () => {
    const {
      id,
      type,
      title,
      desc,
      file,
      icon,
      customIcon,
      sceneIndex,
      sceneName,
    } = isDisplayHotspotPlacement;

    // * Get pitch & yaw
    const [pitch, yaw] = latestPitchYaw;
    const hotspotId = uuidv4();
    let newHotspot = {};

    // * Handle Info type
    if (type === HOTSPOT_TYPE.INFO) {
      newHotspot = {
        id: hotspotId,
        pitch,
        yaw,
        type,
        title,
        desc,
        icon,
      };
    }

    // * Handle Voice Memo type
    if (type === HOTSPOT_TYPE.VOICE_MEMO) {
      newHotspot = {
        id: hotspotId,
        pitch,
        yaw,
        type,
        title,
        desc,
        file,
        icon,
      };
    }

    // * Handle Voice Memo type
    if (type === HOTSPOT_TYPE.PICTURE) {
      newHotspot = {
        id: hotspotId,
        pitch,
        yaw,
        type,
        title,
        file,
        icon,
      };
    }

    // * Handle Swap Scene type
    if (type === HOTSPOT_TYPE.WALK_WAY) {
      newHotspot = {
        id: hotspotId,
        pitch,
        yaw,
        type,
        title,
        icon,
        customIcon,
        sceneIndex,
        sceneName,
      };
    }

    setScenes((curr) => {
      let oldSceneNameExists = undefined;

      const _newScenes = curr.map((scene, i) => {
        // * Set the scene name and update hotspots

        // * If is walk way and scene is the to swap scene then change name to title
        if (type === HOTSPOT_TYPE.WALK_WAY && scene.imageName === sceneName) {
          // * Change other hotspot title to new name aswell
          if (scene.name) {
            // * Scene name already exists
            oldSceneNameExists = _.cloneDeep(scene.name);
          }

          scene.name = title;
          return scene;
        }

        // * If not index of scene being updated then skip
        if (scene.imageName !== selectedImageName) {
          return scene;
        }

        // * Update hotspot
        // * Filter out old hotspot if changing placement
        if (id) {
          scene.hotspots = [
            ...scene.hotspots.filter((h) => h.id !== id),
            newHotspot,
          ];
          return scene;
        }

        scene.hotspots = [...scene.hotspots, newHotspot];
        return scene;
      });

      if (!oldSceneNameExists) {
        return _newScenes;
      }

      // * Change older scene hotspot titles to new name
      return _newScenes.map((scene) => {
        scene.hotspots = scene.hotspots.map((hotspot) => {
          if (
            hotspot.title === oldSceneNameExists &&
            hotspot.type === HOTSPOT_TYPE.WALK_WAY
          ) {
            hotspot.title = title;
          }

          return hotspot;
        });

        return scene;
      });
    });

    // * Remove old hotspot
    if (id) {
      viewer.removeHotSpot(id);
    }

    viewer.removeHotSpot("temp");
    viewer.addHotSpot(
      {
        id: hotspotId,
        pitch,
        yaw,
        text: title,
        type: "custom",
        cssClass: "temp-hotspot",
        createTooltipFunc: createTempHotspot,
        createTooltipArgs: {
          id: hotspotId,
          text: title,
          desc,
          sceneIndex,
          sceneName,
          type,
          iconIndex: icon,
          customIcon,
          file,
          pitch,
          yaw,
        },
      },
      selectedImageName.toString()
    );

    handleCloseOverlay();
  };

  const _handleCancelHotspotPlacement = () => {
    viewer.removeHotSpot("temp");

    const { id } = isDisplayHotspotPlacement;

    // * Unfade existing
    if (id && id !== "temp") {
      const _hotspot = document.getElementById(`temp-hotspot__pin--${id}`);
      if (_hotspot.classList.contains("temp-hotspot__pin--inactive")) {
        _hotspot.classList.remove("temp-hotspot__pin--inactive");
      }
    }

    handleCloseOverlay();
  };

  const handleRemoveFloorPlan = (e) => {
    setFloorPlans((curr) => curr.filter((c) => c.name !== e.name));
  };

  const renderCycleSwitcher = (dayNightMatchIndex) => {
    const match = cycles[dayNightMatchIndex];
    const dayImageIndex = match.day;
    const nightImageIndex = match.night;

    const isDay = dayImageIndex === selectedImageName;

    return (
      <div
        className="new-project-hotspot-pannellum-controls-daynight-toggler"
        data-tooltip-id="my-tooltip"
        data-tooltip-content={`Switch to ${isDay ? "Night" : "Day"}`}
        data-tooltip-place="right"
        onClick={() => {
          setSelectedImageName(isDay ? nightImageIndex : dayImageIndex);
        }}
      >
        <img
          className="new-project-hotspot-pannellum-controls-daynight-toggler__icon"
          alt="icon"
          src={isDay ? cycleNightImage : cycleDayImage}
        />
      </div>
    );
  };

  const handleWatermarkUpload = (files) => {
    // * Skip if no files were uploaded
    if (files.length === 0) {
      return;
    }

    setWatermark(files[0]);
  };

  const handleMusicChange = (music) => {
    setMusic(music);
  };

  const handlePanormaImageUpload = async (files) => {
    const options = {
      maxWidthOrHeight: 4096, // Max width or height in pixels
      useWebWorker: true, // Use multi-threading for performance
    };

    const _files = [];
    const existingFileNames = images.map((image) => image.name);

    for (const file of [...files]) {
      // * Remove duplicate images
      if (existingFileNames.includes(file.name)) {
        continue;
      }

      const _file = await imageCompression(file, options);
      _files.push(_file);
    }

    const _newImagesSet = [...images, ..._files];

    let _newScenes = [];

    // * Add scenes
    for (let i = 0; i < _files.length; i++) {
      const _file = _files[i];
      const sceneId = _file.name;

      _newScenes.push({
        name: "",
        imageIndex: i + images.length,
        imageName: _file.name,
        hotspots: [],
      });

      if (images.length === 0) {
        continue;
      }

      viewer.addScene(
        sceneId,
        {
          ..._baseConfigs,
          imageSource: URL.createObjectURL(_file),
        },
        () => {
          console.log("scene added");
        }
      );
    }

    setImages(_newImagesSet);
    setScenes([...scenes, ..._newScenes]);
    // setDetails((curr) => ({ ...curr, panoramaImages: _newImagesSet }));
    setPanoramaCovers((curr) => [
      ...curr,
      ..._files.map((file) => URL.createObjectURL(file)),
    ]);
    setSelectedImageName(_files[0].name);
  };

  const handleRemovePanoramaImage = (name) => {
    const deletedImageIndex = images.findIndex((i) => i.name === name);

    if (deletedImageIndex === 0 && images.length === 1) {
      setImages([]);
      setScenes([]);
      setMaterialGroups([]);

      setInit(false);
      viewer.destroy();
      viewer = null;
      return;
    }

    // * Remove scene
    setScenes((curr) => {
      let _scenes = [];

      for (const scene of curr) {
        // * Skip the delete scene
        if (scene.imageName === name) continue;

        const _hotspots = [];

        // * Remove references from hotspots
        for (const hotspot of scene.hotspots) {
          if (hotspot.type !== HOTSPOT_TYPE.WALK_WAY) {
            _hotspots.push(hotspot);
            continue;
          }

          if (hotspot.sceneName !== name) {
            _hotspots.push(hotspot);
            continue;
          }

          viewer.removeHotSpot(hotspot.id, scene.imageName);
        }

        scene.hotspots = _hotspots;

        _scenes.push(scene);
      }

      return _scenes;
    });

    // * Remove images
    setImages(images.filter((_, index) => index !== deletedImageIndex));

    // * Remove from material groups
    setMaterialGroups((curr) => {
      if (!curr) return [];

      const _groups = [];

      for (const group of curr) {
        const _match = group.find((material) => material.to === name);

        if (!_match) {
          _groups.push(group);
          continue;
        }

        // * Only remove the deleted scene
        const _remaining = group.filter((material) => material.to !== name);

        // * If group only has 2 then the group must no longer exists
        if (_remaining.length === 1) {
          continue;
        }

        _groups.push(_remaining);
      }

      return _groups;
    });

    // * Remove cycle if present
    setCycles((cycles) => {
      if (!cycles) return [];

      const _cycles = [];

      for (const cycle of cycles) {
        if (cycle.day === name || cycle.night === name) continue;

        _cycles.push(cycle);
      }

      return _cycles;
    });

    if (deletedImageIndex === 0) {
      setSelectedImageName(images[1].name);
      return;
    }

    setSelectedImageName(images[deletedImageIndex - 1].name);
  };

  const handleDragDropEventsPanoramas = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if ((e.type === "dragenter" || e.type === "dragover") && !dragActive) {
      setDragActive(true);
    }

    if ((e.type === "dragleave" || e.type === "drop") && dragActive) {
      setDragActive(false);
    }

    if (e.type === "drop") {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handlePanoramaDragEnd = (result) => {
    const originalIndex = result.source.index;
    const newIndex = result.destination.index;

    // * Swap panorama images
    const _images = _.cloneDeep(images);
    const _draggedImage = _images.splice(originalIndex, 1);
    _images.splice(newIndex, 0, ..._draggedImage);

    setImages(_images);
    setScenes((curr) => {
      const _tmp = curr.map((scene, index) => {
        // * Move image up
        if (originalIndex > newIndex) {
          if (index < newIndex || index > originalIndex) return scene;

          if (index === originalIndex) {
            return {
              ...scene,
              imageIndex: newIndex,
            };
          }

          return {
            ...scene,
            imageIndex: scene.imageIndex + 1,
          };
        }

        // * Move image down
        return {
          ...scene,
        };
      });

      return _tmp;
    });
  };

  const _handleRemoveHotspot = (sceneName, hotspotId) => {
    viewer.removeHotSpot(hotspotId);

    // * Remove from current list of hotspots
    setScenes((curr) => [
      ...curr.map((scene, i) => {
        // * Skip if not the current selection
        if (scene.imageName !== sceneName) {
          return scene;
        }

        // * Update by removing hotspot by id
        scene.hotspots = scene.hotspots.filter((hp) => hp.id !== hotspotId);

        return scene;
      }),
    ]);
  };

  const _handleSwithToPlaceHotspot = (detail) => {
    setIsDisplayHotspotPlacement(detail);
  };

  const _handleAddHotspot = (scene, detail) => {
    const {
      id,
      text: title,
      desc,
      sceneIndex,
      type,
      icon,
      customIcon,
      file,
      pitch,
      yaw,
      sceneName,
    } = detail;

    let newHotspot = {};

    // * Add to hotspot to scenes
    // * Handle Info type
    if (type === HOTSPOT_TYPE.INFO) {
      newHotspot = {
        id,
        pitch,
        yaw,
        type,
        title,
        desc,
        icon,
      };
    }

    // * Handle Voice Memo type
    if (type === HOTSPOT_TYPE.VOICE_MEMO) {
      newHotspot = {
        id,
        pitch,
        yaw,
        type,
        title,
        desc,
        file,
        icon,
      };
    }

    // * Handle Voice Memo type
    if (type === HOTSPOT_TYPE.PICTURE) {
      newHotspot = {
        id: hotspotId,
        pitch,
        yaw,
        type,
        title,
        file,
        icon,
      };
    }

    // * Handle Swap Scene type
    if (type === HOTSPOT_TYPE.WALK_WAY) {
      newHotspot = {
        id,
        pitch,
        yaw,
        type,
        title,
        icon,
        customIcon,
        sceneIndex,
        sceneName,
      };
    }

    setScenes((curr) => [
      ...curr.map((scene, i) => {
        // * Set the scene name and update hotspots

        // * If is walk way and scene is the to swap scene then change name to title
        if (type === HOTSPOT_TYPE.WALK_WAY && scene.imageName === sceneName) {
          scene.name = title;
          return scene;
        }

        // * If not index of scene being updated then skip
        if (scene.imageName !== selectedImageName) {
          return scene;
        }

        scene.hotspots = [...scene.hotspots, newHotspot];
        return scene;
      }),
    ]);

    viewer.addHotSpot(
      {
        id,
        pitch,
        yaw,
        text: title,
        type: "custom",
        cssClass: "temp-hotspot",
        createTooltipFunc: createTempHotspot,
        createTooltipArgs: {
          id,
          text: title,
          desc,
          sceneIndex,
          sceneName,
          type,
          iconIndex: icon,
          customIcon,
          file,
          pitch,
          yaw,
        },
      },
      scene
    );
  };

  const contextValue = useMemo(
    () => ({
      images,
      selectedImageName,
      setSelectedImageName,
      materialGroups,
      setMaterialGroups,
      cycles,
      setCycles,
      latestPitchYaw,
      scenes,
      setScenes,
      switchToPlaceHotspot: _handleSwithToPlaceHotspot,
      closeOverlay: handleCloseOverlay,
      tooltipInfo: isDisplayTooltipInfo,
      panoramaCovers,
      addNewPanoramaImage: handlePanormaImageUpload,
      editHotspot,
      setEditHotspot,
      removeHotspot: _handleRemoveHotspot,
      addHotspot: _handleAddHotspot,
    }),
    [
      images,
      cycles,
      selectedImageName,
      materialGroups,
      latestPitchYaw,
      scenes,
      isDisplayTooltipInfo,
      panoramaCovers,
      editHotspot,
    ]
  );

  const _handlePanoramaLoaded = () => {
    if (init) {
      return;
    }

    setInit(true);

    if (!viewer) return;

    const currentScene = viewer?.getScene();

    if (!currentScene) return;

    if (currentScene === selectedImageName) return;

    setSelectedImageName(currentScene);
  };

  // * =====================
  // *        Render
  // * =====================
  if (pageState !== PAGE_STATE.IDLE) {
    return <>Loading</>;
  }

  // * Check if features are disabled because there is only one image

  const isDisableFeatures = images.length === 1;

  // * Check if there is materials
  const materialExists = materialGroups.find((group) =>
    group.find((m) => m.to === selectedImageName)
  );

  // * Check if there is a cycle binded
  const dayNightMatchIndex = (cycles || []).findIndex(
    (match) =>
      match.day === selectedImageName || match.night === selectedImageName
  );

  return (
    <Context.Provider value={contextValue}>
      <div className="new-project-hotspot">
        <div className="new-project-hotspot-header">
          <h1 className="new-project__heading1">
            {Translation("Setup Hotspots")}
          </h1>
          <div className="new-project-hotspot-header-ctas">
            <div className="new-project-hotspot-header-ctas-action"></div>
          </div>
        </div>
        {images.length > 0 && (
          <div
            className={`new-project-hotspot-panorama-container ${
              isFullscreen
                ? "new-project-hotspot-panorama-container--fullscreen"
                : ""
            }`}
          >
            <ReactPannellum
              id={uuidv4()}
              sceneId={uuidv4()}
              ref={pannellumRef}
              onPanoramaLoaded={_handlePanoramaLoaded}
              imageSource={URL.createObjectURL(images[0] ?? null)}
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
              }}
              config={_baseConfigs}
            />
            {!isDisplayHotspotPlacement && (
              <div className="new-project-hotspot-pannellum-controls">
                <div className="new-project-hotspot-pannellum-controls-left">
                  {materialExists && (
                    <div
                      className="new-project-hotspot-pannellum-controls-material-toggler"
                      onClick={() => setIsDisplayMaterialSwitcher(true)}
                      data-tooltip-id="my-tooltip"
                      data-tooltip-content="Change material"
                      data-tooltip-place="right"
                    >
                      <img
                        className="new-project-hotspot-pannellum-controls-material-toggler__icon"
                        alt="icon"
                        src={materialImage}
                      />
                    </div>
                  )}
                  {dayNightMatchIndex >= 0 &&
                    renderCycleSwitcher(dayNightMatchIndex)}
                </div>
                <div className="new-project-hotspot-pannellum-controls-right">
                  <div
                    className={`new-project-hotspot-pannellum-controls-toggle ${
                      isFullscreen
                        ? "new-project-hotspot-pannellum-controls-toggle--active"
                        : ""
                    }`}
                    onClick={handleToggleHotspot}
                  >
                    <img
                      className="new-project-hotspot-pannellum-controls-toggle__icon"
                      src={toggleHotspotImage}
                      alt="toggle"
                    />
                    <span>{!isFullscreen ? "Toggle Hotspot" : "Exit"}</span>
                  </div>
                  {isFullscreen && (
                    <>
                      <div
                        className={`new-project-hotspot-pannellum-controls-material`}
                        onClick={() => setIsDisplayHotspotWizard(true)}
                        data-tooltip-id="my-tooltip"
                        data-tooltip-content={"Add new hotspot"}
                        data-tooltip-place="left"
                      >
                        <span>Add Hotspot</span>
                      </div>
                      <div
                        className={`new-project-hotspot-pannellum-controls-material`}
                        onClick={() => {
                          setIsDisplayMaterialWizard(true);
                        }}
                      >
                        <span>Add Material</span>
                      </div>
                      {materialExists && (
                        <div
                          className="new-project-hotspot-pannellum-controls-daynight-remover"
                          onClick={() => {
                            setMaterialGroups((curr) => {
                              const _new = [];

                              for (const materialGroup of curr) {
                                const _remaining = materialGroup.filter(
                                  (material) =>
                                    material.to !== selectedImageName
                                );

                                if (_remaining.length === 1) continue;

                                _new.push(_remaining);
                              }

                              return _new;
                            });
                          }}
                        >
                          Unlink Material
                        </div>
                      )}
                      {dayNightMatchIndex < 0 && (
                        <div
                          className={`new-project-hotspot-pannellum-controls-material ${
                            isDisableFeatures
                              ? "new-project-hotspot-pannellum-controls-material--disable"
                              : ""
                          }`}
                          onClick={() => {
                            if (isDisableFeatures) {
                              window.alert(
                                "Add more panoramas to enable this feature!"
                              );
                              return;
                            }

                            setIsDisplayCycleWizard(true);
                          }}
                          data-tooltip-id="my-tooltip"
                          data-tooltip-content={
                            isDisableFeatures ? "No panaromas to select" : ""
                          }
                          data-tooltip-place={"left"}
                        >
                          <span>Add Day/Night</span>
                        </div>
                      )}
                      {dayNightMatchIndex >= 0 && (
                        <div
                          className="new-project-hotspot-pannellum-controls-daynight-remover"
                          onClick={() => {
                            setCycles(
                              cycles.filter((_, i) => i !== dayNightMatchIndex)
                            );
                          }}
                        >
                          Unlink Cycle
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="new-project-hotspot-pannellum-overlays">
              {isDisplayMaterialWizard && <MaterialSelection />}
              {isDisplayMaterialSwitcher && <MaterialSwitcher />}
              {isDisplayCycleWizard && <CycleWizard />}
              {isDisplayHotspotWizard && <AddHotspotWizard />}
              {isDisplayTooltipInfo && <ToolTipInfo />}
              {isDisplayHotspotPlacement && (
                <div className="new-project-hotspot-pannellum-placement">
                  <span>Pan around and click to change hotspot position</span>
                  <div className="new-project-hotspot-pannellum-placement-cta">
                    <button onClick={_handleCancelHotspotPlacement}>
                      Cancel
                    </button>
                    <button onClick={_handleConfirmHotspotPlacement}>
                      Confirm
                    </button>
                  </div>
                </div>
              )}
              {editHotspot && <EditHotspot />}
            </div>
          </div>
        )}
        {images.length === 0 && (
          <div
            className={`new-project-image-upload-fileuploader ${
              dragActive
                ? "new-project-image-upload-fileuploader--highlight"
                : ""
            }`}
            onDragEnter={handleDragDropEventsPanoramas}
            onDragOver={handleDragDropEventsPanoramas}
            onDragLeave={handleDragDropEventsPanoramas}
            onDrop={handleDragDropEventsPanoramas}
          >
            <FileUploaderPlaceholderSvg />
            <p className="new-project-image-upload-fileuploader__info">
              {Translation("Drop your images here or")}{" "}
              <label
                className="new-project-image-upload-fileuploader__input-trigger"
                htmlFor="new-project-image-upload-fileuploader__input"
              >
                {Translation("browse")}
              </label>
              <br />
              {Translation("Supports: JPG and PNG")}
            </p>
            <input
              id="new-project-image-upload-fileuploader__input"
              type="file"
              multiple
              accept="image/*"
              onClick={(e) => {
                e.target.value = null;
              }}
              onChange={(e) => handlePanormaImageUpload(e.target.files)}
            />
          </div>
        )}
        {images.length > 0 && (
          <DragDropContext onDragEnd={handlePanoramaDragEnd}>
            <div className="new-project-image-upload-gallery-scroller">
              <Droppable droppableId="panoramas" direction="horizontal">
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    className="new-project-image-upload-gallery"
                    {...provided.droppableProps}
                  >
                    <label
                      className="new-project-hotspot-panorama-uploader"
                      htmlFor="new-project-hotspot-panorama-uploader__input"
                      style={{ textAlign: "center" }}
                    >
                      <FileUploaderPlaceholderSvg height={36} />
                      Click to add more panoramas
                      <input
                        id="new-project-hotspot-panorama-uploader__input"
                        type="file"
                        multiple
                        accept="image/*"
                        onClick={(e) => {
                          e.target.value = null;
                        }}
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handlePanormaImageUpload(e.target.files)
                        }
                      />
                    </label>
                    {[...images].map((file, index) => {
                      const _src = URL.createObjectURL(file);

                      const className = [
                        "new-project-image-upload-gallery-image",
                      ];

                      // if (selectedImageName === file.name)
                      //   className.push(
                      //     "new-project-image-upload-gallery-image--selected"
                      //   );

                      return (
                        <Draggable draggableId={index.toString()} index={index}>
                          {(provided) => (
                            <div
                              key={file.name}
                              className={className.join(" ")}
                              ref={provided.innerRef}
                              {...provided.dragHandleProps}
                              {...provided.draggableProps}
                              onClick={() => setSelectedImageName(file.name)}
                            >
                              <img
                                className="new-project-image-upload-gallery-image__preview"
                                alt="preview_image"
                                src={_src}
                              />
                              <TrashSvg
                                className="new-project-image-upload-gallery-image__trash"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemovePanoramaImage(file.name);
                                }}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>
        )}
        <span className="new-project__heading2">
          {Translation("Select background music (optional)")}
        </span>
        <SelectMusic defaultMusic={music} onChange={handleMusicChange} />
        <div className="new-project__heading2">
          {Translation("Floor Plans (optional)")}
        </div>
        <div
          className={`new-project-floorplan-image-upload-fileuploader ${
            dragActive
              ? "new-project-floorplan-image-upload-fileuploader--highlight"
              : ""
          }`}
          onDragEnter={handleDragDropEvents}
          onDragOver={handleDragDropEvents}
          onDragLeave={handleDragDropEvents}
          onDrop={handleDragDropEvents}
        >
          <FileUploaderPlaceholderSvg />
          <p className="new-project-floorplan-image-upload-fileuploader__info">
            {Translation("Drop your images here or")}{" "}
            <label
              className="new-project-floorplan-image-upload-fileuploader__input-trigger"
              htmlFor="new-project-floorplan-image-upload-fileuploader__input"
            >
              {Translation("browse")}
            </label>
            <br />
            {Translation("Supports: JPG and PNG")}
          </p>
          <input
            id="new-project-floorplan-image-upload-fileuploader__input"
            type="file"
            multiple
            accept="image/*"
            onClick={(e) => {
              e.target.value = null;
            }}
            onChange={(e) => handleFloorPlanFiles(e.target.files)}
          />
        </div>
        {[...floorPlans].length > 0 && (
          <div className="new-project-floorplan-image-upload-gallery-scroller">
            <div className="new-project-floorplan-image-upload-gallery">
              {[...floorPlans].map((file, index) => {
                const _src = URL.createObjectURL(file);
                return (
                  <div className="new-project-floorplan-image-upload-gallery-image">
                    <img
                      className="new-project-floorplan-image-upload-gallery-image__preview"
                      alt="preview image"
                      src={_src}
                    />
                    <input
                      className={`new-project-floorplan-image-upload-gallery-image__name ${
                        !floorPlanNames.find((f) => f.fileName === file.name)
                          ?.name
                          ? "new-project-floorplan-image-upload-gallery-image__name--error"
                          : ""
                      }`}
                      value={
                        floorPlanNames.find((f) => f.fileName === file.name)
                          ?.name ?? ""
                      }
                      onChange={(e) => {
                        const _newName = e.target.value;

                        setFloorPlanNames((curr) =>
                          curr.map((fp) => {
                            if (fp.fileName !== file.name) {
                              return fp;
                            }

                            fp.name = _newName;
                            return fp;
                          })
                        );
                      }}
                    />
                    <TrashSvg
                      className="new-project-floorplan-image-upload-gallery-image__trash"
                      onClick={() => handleRemoveFloorPlan(file)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* <div className="new-project__heading2">
          {Translation("Watermark (optional)")}
        </div>
        <div
          className={`new-project-floorplan-image-upload-fileuploader ${
            dragActive
              ? "new-project-floorplan-image-upload-fileuploader--highlight"
              : ""
          }`}
          onDragEnter={handleDragDropEvents}
          onDragOver={handleDragDropEvents}
          onDragLeave={handleDragDropEvents}
          onDrop={handleDragDropEvents}
        >
          <FileUploaderPlaceholderSvg />
          <p className="new-project-floorplan-image-upload-fileuploader__info">
            {Translation("Drop your images here or")}{" "}
            <label
              className="new-project-floorplan-image-upload-fileuploader__input-trigger"
              htmlFor="new-project-watermark-uploader"
            >
              {Translation("browse")}
            </label>
            <br />
            {Translation("Supports: PNG")}
          </p>
          <input
            id="new-project-watermark-uploader"
            type="file"
            accept=".png"
            onClick={(e) => {
              e.target.value = null;
            }}
            onChange={(e) => handleWatermarkUpload(e.target.files)}
          />
          {watermark && (
            <div className="new-project-watermark-image">
              <div className="new-project-watermark-image-container">
                <img
                  className="new-project-watermark-image-container__image"
                  alt="watermark"
                  src={URL.createObjectURL(watermark)}
                />
              </div>
              <div
                className="new-project-watermark-image__cancel"
                onClick={() => {
                  setWatermark(null);
                }}
              >
                <TrashWhiteSvg width={20} onClick={() => {}} />
              </div>
            </div>
          )}
        </div> */}
        <div className="new-project-hotspot-footer">
          <Button
            label={TranslationCommon("Return")}
            variant={"secondary"}
            leadingIcon={<ArrowLeftSvg />}
            onClick={handleReturn}
          />
          <Button
            label={TranslationCommon("Continue")}
            trailingIcon={<ArrowRightSvg />}
            onClick={handleContinue}
          />
        </div>
      </div>
      {/* <LoadCover ref={loadCoverRef} onLoaded={handleCoverLoaded} /> */}
      <Tooltip.Tooltip id="my-tooltip" />
    </Context.Provider>
  );
};

SetupHotspot.propTypes = {};

export default SetupHotspot;
